import React, { useState, useEffect } from 'react';
import { securePut } from '../../config/api';
import { Helmet } from "react-helmet-async";
import { useTranslation } from 'react-i18next';

import { formatAmount } from '../../utils/format';
import { SaveCancelTop } from '../../components/PrimaryButton';
import Input from '../../components/Input';
import SnackbarNotification from '../../components/SnackbarNotification';
import Breadcrumbs from "../../components/Breadcrumbs";

import plusIcon from '../../icons/plus.svg';
import deleteIcon from '../../icons/trashR.svg';
import plus from '../../icons/plusP.svg';
import minus from '../../icons/minusP.svg';
import noImage from '../../icons/noImageIcon.svg';

const PurchaseForm = ({
  title,
  baseFields = [],
  listFields = [],
  additionalFields = [],
  products = [],
  onSubmit,
  onSuccess,
  onCancel,
}) => {
  const [formValues, setFormValues] = useState({});
  const [productList, setProductList] = useState([]);
  const [currentProduct, setCurrentProduct] = useState({
    productName: '',
    quantity: '',
    unitPrice: '',
  });
  const { t } = useTranslation();
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [saving, setSaving] = useState(false);

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  useEffect(() => {
    const initialValues = {};
    baseFields.forEach((field) => {
      initialValues[field.name] = field.value || '';
    });
    setFormValues(initialValues);
  }, [baseFields]);

  const handleInputChange = (name, value) => {
    setFormValues((prevValues) => ({ ...prevValues, [name]: value }));
  };

  const handleListFieldChange = (name, value) => {
    setCurrentProduct((prevProduct) => ({
      ...prevProduct,
      [name]: value,
    }));
  };

  const addProductToList = () => {
    const { productName, quantity, unitPrice } = currentProduct;


    if (!productName || !quantity || !unitPrice) {
      showSnackbar(t('placeholder.empty_fields'), 'error');
      return;
    }

    const selectedProduct = products.find(
      (product) => product.value === productName
    );


    setProductList((prevList) => [
      ...prevList,
      {
        reference: selectedProduct.reference,
        productName: selectedProduct.name,
        id: selectedProduct.value,
        quantity: parseInt(quantity, 10),
        unitPrice: parseFloat(unitPrice).toFixed(2),
        image: selectedProduct.fullEntity.image,
      },
    ]);

    setCurrentProduct({ productName: "", quantity: "", unitPrice: "" });
  };


  const removeProductFromList = (index) => {
    setProductList((prevList) => prevList.filter((_, i) => i !== index));
  };

  const handleQuantityChange = (index, newQuantity) => {
    setProductList((prevList) =>
      prevList.map((product, i) =>
        i === index ? { ...product, quantity: Math.max(newQuantity, 0) } : product
      )
    );
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // In modify mode, listFields are not required if productList already exists
    const fieldsToValidate = productList.length > 0 
        ? [...baseFields, ...additionalFields] 
        : [...baseFields, ...listFields, ...additionalFields];
        
    const missing = fieldsToValidate.filter(f => f.required && !formValues[f.name]);
    if (missing.length > 0) {
      showSnackbar(`Veuillez remplir les champs obligatoires : ${missing.map(f => f.label).join(', ')}`, 'error');
      return;
    }

    const payload = {
      products: productList.map((product) => ({
        productId: product.id,
        price: parseFloat(product.unitPrice),
        quantity: product.quantity,
      })),
      providerId: parseInt(formValues.fournisseur, 10),
      purchaseDate: formValues.dateFacture.date,
      purchaseNumber: formValues.factureNumber,
      repoId: parseInt(formValues.stockAlimenter, 10),
    };

    setSaving(true);
    try {
      const response = await securePut('purchases/add', payload, showSnackbar);

      if (response.status === 200 || response.status === 201) {
        onSuccess();
        onSubmit(response.data);

      }
    } catch (error) {
      console.error('Error submitting purchase:', error);
    } finally {
      setSaving(false);
    }
  };

  const handleImageError = (e) => {
    e.target.src = noImage;
    e.target.style.width = "25px";
    e.target.style.height = "25px";
    e.target.style.objectFit = "contain";
  };


  return (

    <div className="w-full">
      <Helmet>
        <title>GrayFox - {t("titles.purchases")}</title>
      </Helmet>

      <div className="flex flex-col space-y-2">
        <Breadcrumbs />
        <div className="flex justify-between items-center">
          <h2 className="text-[32px] font-bold text-black">{title}</h2>
          <SaveCancelTop onCancel={onCancel} onSave={handleSubmit} saving={saving} />
        </div>
      </div>


      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="mt-4">
          {/* <div className="grid grid-cols- md:grid-cols-4 gap-4"> */}
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">

            {baseFields.map((field, index) => (
              <Input
                key={index}
                type={field.type || 'text'}
                label={field.label}
                name={field.name}
                value={formValues[field.name] || ''}
                required={field.required}
                onChange={(value) => handleInputChange(field.name, value)}
                options={field.options || []}
              />
            ))}
          </div>
        </div>

        {/* Additional Fields */}
        {additionalFields.length > 0 && (
          <div className="space-y-[10px]">
            <h3 className="text-[16px] font-semibold text-[#ACAFC6]">{t(`fields_types.additional`)}</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {additionalFields.map((field, index) => (
                <Input
                  key={index}
                  label={field.label}
                  name={field.name}
                  type={field.type || "text"}
                  value={formValues[field.name] || ""}
                  required={field.required}
                  onChange={(value) => handleInputChange(field.name, value)}
                  options={field.options || []}
                />
              ))}
            </div>
          </div>
        )}

        <div>
          <h3 className="text-[16px] font-medium text-[#ACAFC6] mb-[10px]">{t(`fields_types.products`)}</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {listFields.map((field, index) => (
              <Input
                key={index}
                type={field.type || 'text'}
                label={field.label}
                name={field.name}
                required={field.required}
                value={currentProduct[field.name] || ''}
                onChange={(value) => handleListFieldChange(field.name, value)}
                options={
                  field.name === 'productName'
                    ? products.map((product) => ({
                      value: product.value,
                      name: product.name,
                      reference: product.reference,
                      fullEntity: product.fullEntity,
                    }))
                    : field.options || []
                }
              />
            ))}
            <button
              type="button"
              onClick={addProductToList}
              className="bg-[#043F88] text-white w-[48px] h-[48px] rounded-md hover:bg-[#3F3456] flex items-center justify-center"
            >
              <img src={plusIcon} alt="Add" className="w-4 h-4" />
            </button>
          </div>
        </div>

        {productList.length > 0 && (
          <div className="mt-4">
            {/* Header Row */}
            <div className="grid grid-cols-[2fr_1fr_1fr_0.5fr] items-center text-[#919191] text-sm font-semibold mb-2 px-6">
              <div className="text-left">
                <span>{t(`rows.product`)}</span>
              </div>
              <div className="text-center">
                <span>{t(`rows.priceuHT`)}</span>
              </div>
              <div className="text-center">
                <span>{t(`rows.quantity`)}</span>
              </div>
              <div className="text-right">
                <span>{ }</span>
              </div>
            </div>

            {/* Data Rows */}
            {productList.map((product, index) => (
              <div
                key={index}
                className="grid grid-cols-[2fr_1fr_1fr_0.5fr] items-center py-4 px-6 rounded-[12px] bg-white shadow-md mb-2"
              >
                {/* Product Information (Aligned Left) */}
                <div className="flex items-center gap-4 text-left">
                  <div className="w-12 h-12 flex items-center justify-center flex-shrink-0 bg-gray-100 rounded-md overflow-hidden">
                    <img
                      src={product.image}
                      alt={product.productName}
                      className="w-full h-full object-cover"
                      onError={handleImageError}
                    />
                  </div>
                  <div>
                    <p className="text-[#043F88] text-[12px] font-semibold">
                      {product.reference || "Unknown Reference"}
                    </p>
                    <p className="text-[#6B78F8] text-[16px] font-semibold">
                      {product.productName || "Unknown Product"}
                    </p>
                  </div>
                </div>

                {/* Unit Price (Centered) */}
                <div className="text-center text-[#043F88] text-[18px] font-semibold">
                  {formatAmount(product.unitPrice)}
                </div>

                {/* Quantity Selector (Centered) */}
                <div className="flex justify-center">
                  <div className="flex items-stretch rounded-[12px] border border-[#6C78F8] bg-[#6B78F858] divide-x divide-[#6C78F8]">
                    <button
                      type="button"
                      onClick={() =>
                        handleQuantityChange(index, Math.max(product.quantity - 1, 1))
                      }
                      className="w-8 h-8 flex items-center justify-center text-[#6C78F8] font-bold"
                    >
                      <img src={minus} alt="Minus" className="w-4 h-4" />
                    </button>
                    <span className="w-8 h-8 flex items-center justify-center font-semibold text-[#38485C] text-[14px]">
                      {product.quantity}
                    </span>
                    <button
                      type="button"
                      onClick={() =>
                        handleQuantityChange(index, product.quantity + 1)
                      }
                      className="w-8 h-8 flex items-center justify-center text-[#6C78F8] font-bold"
                    >
                      <img src={plus} alt="Plus" className="w-4 h-4" />
                    </button>
                  </div>
                </div>

                {/* Delete Button (Right-Aligned) */}
                <div className="flex justify-end">
                  <button
                    className="w-8 h-8 flex items-center justify-center bg-[#F2F3F3] rounded-[12px] shadow-md cursor-pointer hover:bg-red-50"
                    onClick={() => removeProductFromList(index)}
                  >
                    <img src={deleteIcon} alt="Delete" className="w-4 h-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </form>
      <SnackbarNotification
        open={snackbar.open}
        message={snackbar.message}
        severity={snackbar.severity}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
      />
    </div>
  );
};

export default PurchaseForm;
